import { useMemo, useState, type FormEvent } from "react";
import { createRoute } from "@tanstack/react-router";
import { staffRoute } from "./staff";
import {
  useFacilitatorsQuery,
  useInviteFacilitatorMutation,
  useRevokeFacilitatorMutation,
} from "@/lib/staff/facilitator-mutations";
import type {
  Facilitator,
  FacilitatorStatus,
  RevokeMode,
} from "@/lib/staff/facilitator-types";
import styles from "./staff-facilitators.module.scss";

const STATUS_LABELS: Record<FacilitatorStatus, string> = {
  pending: "Pending",
  active: "Active",
  declined: "Declined",
  revoked: "Revoked",
};

function statusGroup(rows: Facilitator[]): {
  active: Facilitator[];
  pending: Facilitator[];
  inactive: Facilitator[];
} {
  const active: Facilitator[] = [];
  const pending: Facilitator[] = [];
  const inactive: Facilitator[] = [];
  for (const row of rows) {
    if (row.status === "active") active.push(row);
    else if (row.status === "pending") pending.push(row);
    else inactive.push(row);
  }
  return { active, pending, inactive };
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function userLabel(row: Facilitator): {
  primary: string;
  secondary: string;
} {
  const u = row.user;
  if (!u) return { primary: row.userId, secondary: "" };
  const primary = u.displayName || u.username || u.email;
  const secondary =
    u.username && u.username !== primary ? `@${u.username}` : u.email;
  return { primary, secondary };
}

function StaffFacilitators() {
  const query = useFacilitatorsQuery();
  const inviteMut = useInviteFacilitatorMutation();
  const revokeMut = useRevokeFacilitatorMutation();

  const [inviteValue, setInviteValue] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);

  const groups = useMemo(() => statusGroup(query.data ?? []), [query.data]);

  async function handleInviteSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInviteError(null);
    const trimmed = inviteValue.trim();
    if (!trimmed) return;

    const isEmail = trimmed.includes("@");
    try {
      await inviteMut.mutateAsync(
        isEmail ? { email: trimmed } : { userId: trimmed },
      );
      setInviteValue("");
    } catch (err) {
      setInviteError((err as Error)?.message ?? "Failed to send invite");
    }
  }

  async function handleRevoke(row: Facilitator, mode: RevokeMode) {
    const verb = mode === "force" ? "force-revoke" : "revoke";
    const reason = window.prompt(`Reason for ${verb} (optional):`, "");
    if (reason === null) return;
    try {
      await revokeMut.mutateAsync({
        userId: row.userId,
        input: { mode, reason: reason.trim() || undefined },
      });
    } catch (err) {
      // surface via mutation state; for now show alert
      window.alert((err as Error)?.message ?? "Failed to revoke");
    }
  }

  return (
    <main className={styles.shell}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Facilitators</h1>
          <p className={styles.sub}>
            {query.data
              ? `${query.data.length} total — eligibility ledger`
              : "Loading facilitators"}
          </p>
        </div>
      </header>

      <section className={styles.inviteCard}>
        <h2 className={styles.inviteCardHead}>Invite a facilitator</h2>
        <form className={styles.inviteForm} onSubmit={handleInviteSubmit}>
          <input
            className={styles.inviteInput}
            type="text"
            value={inviteValue}
            onChange={(e) => setInviteValue(e.target.value)}
            placeholder="email@example.com or user-id"
            aria-label="Invitee email or user id"
            disabled={inviteMut.isPending}
          />
          <button
            type="submit"
            className={styles.inviteBtn}
            disabled={inviteMut.isPending || inviteValue.trim().length === 0}
          >
            {inviteMut.isPending ? "Inviting…" : "Send invite"}
          </button>
        </form>
        {inviteError ? (
          <div className={styles.errorBanner} role="alert">
            {inviteError}
          </div>
        ) : null}
      </section>

      {query.isError ? (
        <div className={styles.errorBanner} role="alert">
          Failed to load facilitators:{" "}
          {(query.error as Error)?.message ?? "Unknown error"}
        </div>
      ) : null}

      <FacilitatorSection
        title="Active"
        rows={groups.active}
        onRevoke={handleRevoke}
        canRevoke
      />
      <FacilitatorSection
        title="Pending invites"
        rows={groups.pending}
        onRevoke={handleRevoke}
      />
      <FacilitatorSection
        title="Past"
        rows={groups.inactive}
        onRevoke={handleRevoke}
      />
    </main>
  );
}

function FacilitatorSection({
  title,
  rows,
  onRevoke,
  canRevoke = false,
}: {
  title: string;
  rows: Facilitator[];
  onRevoke: (row: Facilitator, mode: RevokeMode) => void;
  canRevoke?: boolean;
}) {
  return (
    <section className={styles.section}>
      <header className={styles.sectionHead}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <span className={styles.sectionCount}>
          {rows.length} {rows.length === 1 ? "person" : "people"}
        </span>
      </header>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead className={styles.tHead}>
            <tr>
              <th>User</th>
              <th>Status</th>
              <th>Invited</th>
              <th>Accepted</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const { primary, secondary } = userLabel(row);
              return (
                <tr key={row.id} className={styles.row}>
                  <td>
                    <div className={styles.userCell}>
                      <span className={styles.userName}>{primary}</span>
                      {secondary ? (
                        <span className={styles.userMeta}>{secondary}</span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <span className={styles.statusBadge}>
                      {STATUS_LABELS[row.status]}
                    </span>
                  </td>
                  <td>{formatDate(row.invitedAt)}</td>
                  <td>{formatDate(row.acceptedAt)}</td>
                  <td>
                    <div className={styles.actions}>
                      {canRevoke && row.status === "active" ? (
                        <>
                          <button
                            type="button"
                            className={styles.revokeBtn}
                            onClick={() => onRevoke(row, "graceful")}
                          >
                            Revoke
                          </button>
                          <button
                            type="button"
                            className={styles.revokeBtn}
                            onClick={() => onRevoke(row, "force")}
                          >
                            Force revoke
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className={styles.empty}>
                  None.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export const staffFacilitatorsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/facilitators",
  component: StaffFacilitators,
});
